import React, { useState, useEffect } from 'react';
import { roleService } from '../../services/roleService';
import { permissionService } from '../../services/permissionService';
import { HmgRole, GroupedPermissions } from '../../types';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Loading from '../../components/common/Loading';

interface RolePermissionsModalProps {
  role: HmgRole;
  onClose: () => void;
  onSuccess: () => void;
}

const RolePermissionsModal: React.FC<RolePermissionsModalProps> = ({ role, onClose, onSuccess }) => {
  const [groupedPermissions, setGroupedPermissions] = useState<GroupedPermissions[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [role.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [grouped, rolePermissions] = await Promise.all([
        permissionService.getGrouped(),
        roleService.getPermissions(role.id),
      ]);
      setGroupedPermissions(grouped);
      setSelectedPermissions(rolePermissions.map((p) => p.id));
    } catch (err: any) {
      setError('Erro ao carregar permissões');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = (permissionId: number) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleToggleModule = (modulePermissions: number[]) => {
    const allSelected = modulePermissions.every((id) => selectedPermissions.includes(id));
    if (allSelected) {
      setSelectedPermissions((prev) => prev.filter((id) => !modulePermissions.includes(id)));
    } else {
      setSelectedPermissions((prev) => {
        const combined = [...prev, ...modulePermissions];
        // Remove duplicatas manualmente
        return combined.filter((id, index) => combined.indexOf(id) === index);
      });
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      await roleService.syncPermissions(role.id, selectedPermissions);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={`Permissões - ${role.name}`} size="lg">
      <div className="p-6">
        {error && (
          <Alert type="error" className="mb-4" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loading size="lg" />
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-gray-600">
              Selecione as permissões que este perfil deve ter acesso
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {groupedPermissions.map((group) => {
                const modulePermissionIds = group.permissions.map((p) => p.id);
                const allSelected = modulePermissionIds.every((id) =>
                  selectedPermissions.includes(id)
                );
                const someSelected = modulePermissionIds.some((id) =>
                  selectedPermissions.includes(id)
                );

                return (
                  <div key={group.module} className="border rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(input) => {
                          if (input) {
                            input.indeterminate = someSelected && !allSelected;
                          }
                        }}
                        onChange={() => handleToggleModule(modulePermissionIds)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm font-semibold text-gray-900 uppercase">
                        {group.module}
                      </label>
                    </div>

                    <div className="grid grid-cols-2 gap-2 ml-6">
                      {group.permissions.map((permission) => (
                        <div key={permission.id} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`perm-${permission.id}`}
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={() => handleTogglePermission(permission.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label
                            htmlFor={`perm-${permission.id}`}
                            className="ml-2 text-sm text-gray-700"
                          >
                            {permission.action}
                            {permission.description && (
                              <span className="text-gray-500"> - {permission.description}</span>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <div className="text-sm text-gray-600">
                {selectedPermissions.length} permissões selecionadas
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                  Cancelar
                </Button>
                <Button type="button" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar Permissões'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default RolePermissionsModal;
